import {
  Box,
  type BoxProps,
  Flex,
  HStack,
  Image as ChakraImage,
  Stack,
  Text,
} from '@chakra-ui/react';
import type {
  PortableTextComponentProps,
  ReactPortableTextList,
} from '@portabletext/react';
import type {
  PortableTextBlock,
  PortableTextListItemBlock,
} from '@portabletext/types';
import { Image } from '@time-line/components/common';
import { VerifiedTick } from '@time-line/components/icons';
import type { SanityImage } from '@time-line/components/types';
import { clamp } from '@time-line/components/utils';
import { RichTextResolver } from 'components/common/RichTextResolver';

const OVERLAY_SIZE = clamp(90, 120);

const CredentialsBulletList = ({
  children,
}: PortableTextComponentProps<ReactPortableTextList>) => (
  <Stack as="ul" spacing="2px" listStyleType="none">
    {children}
  </Stack>
);

const CredentialsBulletItem = ({
  children,
}: PortableTextComponentProps<PortableTextListItemBlock>) => (
  <HStack as="li" gap="4xs" align="center" color="gray.600">
    <VerifiedTick width="2xs" height="2xs" />
    <Text size="detail" fontWeight="medium">
      {children}
    </Text>
  </HStack>
);

interface CredentialsCardIcon {
  _key?: string;
  label: string;
  asset: { url: string };
}

interface CredentialsCardProps extends BoxProps {
  overlayImage?: SanityImage;
  icons?: CredentialsCardIcon[];
  bullets?: PortableTextBlock[];
}

export function CredentialsCard({
  overlayImage,
  icons,
  bullets,
  ...props
}: CredentialsCardProps) {
  return (
    <Box
      pos="relative"
      w="full"
      maxW="375px"
      bg="gray.100"
      borderRadius="base"
      p={{ base: '2xs', lg: 'sm' }}
      {...props}
    >
      {overlayImage && (
        <Box
          pos="absolute"
          top={0}
          right={0}
          boxSize={OVERLAY_SIZE}
          transform="translate(-10%, -35%)"
          pointerEvents="none"
        >
          <Image
            image={overlayImage}
            alt={overlayImage.alt?.trim() ?? ''}
            builderOptions={{ fill: false }}
            sizes="120px"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>
      )}
      <Text size="title2" mb="2xs">
        Developed by scientists.
        <br />
        Third-party tested.
      </Text>
      <Flex direction="row" align="flex-end" justify="space-between" gap="3xs">
        {bullets && bullets.length > 0 && (
          <RichTextResolver
            blocks={bullets}
            list={{ bullet: CredentialsBulletList }}
            listItem={CredentialsBulletItem}
          />
        )}
        {icons && icons.length > 0 && (
          <HStack spacing="4xs">
            {icons.map((icon) => (
              <ChakraImage
                key={icon._key ?? icon.label}
                src={icon.asset.url}
                alt={icon.label}
                h="xl"
                w="auto"
              />
            ))}
          </HStack>
        )}
      </Flex>
    </Box>
  );
}
